import { useQuery } from '@tanstack/react-query';

import { useAppContext } from '@contexts/AppContext';
import { UserSeriesPlannerPlans } from '@shared/types/series';

import API from '@api/API';

import { useCurrentUserCity, useCurrentUserLocation } from '@hooks/useCurrentUserLocation';

import { CritiqueData, UserCritique } from './CritiqueQueries';
import { Entertainer } from './EntertainerQueries';
import { FeedEvent } from './EventQueries';
import { useCurrentUser } from './UserQueries';

export interface Series {
  _id: string;
  slug: string;
  title: string;
  shareUrl?: string;
  showsTitleOverride?: string;
  showsCalendarTitleOverride?: string;
  description?: string;
  city: {
    _id: string;
    name: string;
  };
  cities: { _id: string; name: string }[];
  filterTags: { _id: string; label: string }[];
  isEntertainerSeries?: boolean;
  isLocalDragSeries?: boolean;
  isFeatureApplicationEnabled?: boolean;
  entertainerCategories: string[];
  showEntertainerFeed?: boolean;
  showRecommendedTab?: boolean;
  showEntertainerShows?: boolean;
  calendarPost?: {
    startDate?: string;
    endDate?: string;
  };
  hasInstagramHandles?: boolean;
  hideOpenStages?: boolean;
  primaryEntertainerType?: 'dragPerformer' | 'dj' | 'comedian';
  lineupReferrers?: Record<string, string>;
  lastShowFeaturePostDate?: string;
  primaryCtas?: { img: string; title: string; description: string; url: string }[];
}

export const useSeries = () => {
  const { data, isFetched, refetch } = useQuery({
    queryKey: ['series'],
    queryFn: () => API.get<{ series: Series[] }>(`/v1/series`),
  });

  return { series: data?.series, isFetched, refetch };
};

interface SeriesHost {
  title: string;
  description: string;
  image: string;
  to: string;
}

export interface SeriesBonusTip {
  _id: string;
  payoutAmount?: number;
  competitionDates?: {
    startDate: string;
    endDate: string;
  };
  eligibleShowDates?: {
    startDate: string;
    endDate: string;
  };
}

export const useSeriesByTagSlug = (
  tagSlug: string,
  options?: {
    onSuccess?: (data?: {
      series: Series;
      seriesHost?: SeriesHost;
      seriesBonusTip?: SeriesBonusTip;
    }) => void;
  },
) => {
  const { data, isFetched, refetch } = useQuery({
    queryKey: ['series', tagSlug],
    queryFn: () =>
      API.get<{
        series: Series;
        seriesHost?: SeriesHost;
        seriesBonusTip?: SeriesBonusTip;
      }>(`/v1/series/${tagSlug}`),
    enabled: !!tagSlug,
    select: (data) => {
      options?.onSuccess?.(data);
      return data;
    },
  });

  return {
    series: data?.series,
    seriesHost: data?.seriesHost,
    seriesBonusTip: data?.seriesBonusTip,
    isFetched,
    refetch,
  };
};

export const useSeriesByTagId = (tagId: string) => {
  const { data, isFetched, refetch } = useQuery({
    queryKey: ['series', tagId],
    queryFn: () =>
      API.get<{
        series: Series;
        seriesHost?: SeriesHost;
        recommendations: {
          sections: {
            title: string;
            imgUrl: string;
            count: number;
            to: string;
          }[];
        };
      }>(`/v1/tags/${tagId}/series`),
    enabled: !!tagId,
  });

  return {
    series: data?.series,
    seriesHost: data?.seriesHost,
    recommendations: data?.recommendations,
    isFetched,
    refetch,
  };
};

export const useSeriesEntertainersQuery = (tagId: string, isEnabled = true) => {
  const { data, isFetched, refetch } = useQuery({
    queryKey: useSeriesEntertainersQuery.queryKey(tagId),
    queryFn: () => API.get<{ entertainers: Entertainer[] }>(`/v1/series/${tagId}/entertainers`),
    enabled: !!tagId,
    staleTime: 1000 * 15 * 60,
  });

  return { entertainers: data?.entertainers, isFetched, refetch };
};

useSeriesEntertainersQuery.queryKey = (tagId: string) => ['series', tagId, 'entertainers'];

export const useSeriesInstagramHandleLatestPostsQuery = (tagId: string | undefined) => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: useSeriesInstagramHandleLatestPostsQuery.queryKey(tagId),
    queryFn: () => useSeriesInstagramHandleLatestPostsQuery.queryFn(tagId),
    enabled: !!tagId,
  });

  return {
    seriesInstagramHandleLatestPostsAreReady: isFetched,
    seriesInstagramHandleLatestPosts: data,
    refetchSeriesInstagramHandleLatestPosts: refetch,
  };
};
useSeriesInstagramHandleLatestPostsQuery.queryKey = (tagId: string | undefined) => [
  'series',
  tagId,
  'instagram-handle-latest-posts',
];
useSeriesInstagramHandleLatestPostsQuery.queryFn = async (
  tagId: string | undefined,
  username?: string,
) => {
  if (!tagId) return undefined;

  const queryString = username ? `?username=${username}` : '';

  return API.get<{
    results: any[];
  }>(`/v1/series/${tagId}/instagram-handle-posts${queryString}`);
};

export const useSeriesPostersReviewsQuery = (tagSlug: string) => {
  const { data, isFetched, refetch } = useQuery({
    queryKey: ['seriesPostersReviews', tagSlug],
    queryFn: () =>
      API.get<{
        events: (FeedEvent & { reviewsCount: number })[];
      }>(`/v1/series/${tagSlug}/posters-reviews`),
    enabled: !!tagSlug,
  });

  return {
    seriesPosters: data?.events,
    areSeriesPostersReviewsFetched: isFetched,
    refetchSeriesPostersReviews: refetch,
  };
};

export const useCurrentUserSeriesPlannerPlansQuery = () => {
  const { currentUser } = useCurrentUser();
  const userId = currentUser?._id;

  const { data, isFetched } = useQuery({
    queryKey: useCurrentUserSeriesPlannerPlansQuery.queryKey(userId),
    queryFn: () =>
      userId
        ? API.get<{ seriesPlannerPlans: Record<string, UserSeriesPlannerPlans[]> }>(
            '/v1/user/series-planner-plans',
          )
        : Promise.resolve({ seriesPlannerPlans: {} }),
  });

  return {
    seriesPlannerPlans: data?.seriesPlannerPlans,
    seriesPlannerPlansAreReady: isFetched,
  };
};

useCurrentUserSeriesPlannerPlansQuery.queryKey = (userId: string | undefined) => [
  'user',
  userId ?? 'unauthenticated',
  'series-planner-plans',
];
